import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PROFILE_TYPE_LABEL, PROFILE_TYPES, MAX_PROFILES_PER_TYPE } from "@/lib/constants";
import { locationLine } from "@/lib/format";
import EmptyState from "@/components/shared/EmptyState";
import DeleteProfileDialog from "@/components/shared/DeleteProfileDialog";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";

export default function MyProfiles() {
  const { profiles, account, switchProfile, refresh } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({});
  const [toDelete, setToDelete] = useState(null);

  const loadCounts = useCallback(
    () => api.get("/profiles/count-by-type").then(({ data }) => setCounts(data.counts || {})).catch(() => {}),
    [],
  );

  useEffect(() => { loadCounts(); }, [profiles, loadCounts]);

  return (
    <AppLayout>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl font-bold tracking-tight">My Profiles</h1>
        {profiles.length === 0 && (
          <Button onClick={() => nav("/profiles/add")} data-testid="add-profile"
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
            <Plus className="w-4 h-4 mr-1" /> Add profile
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        {PROFILE_TYPES.map((t) => {
          const existingProfile = profiles.find((p) => p.profile_type === t);
          const n = Math.max(Number(counts[t] || 0), profiles.filter((p) => p.profile_type === t).length);
          const limitReached = n >= MAX_PROFILES_PER_TYPE;
          return (
            <Card key={t} className={`p-3 border-slate-200 ${limitReached ? "opacity-70" : ""}`} data-testid={`count-${t}`}>
              <div className="text-xs uppercase text-slate-500 tracking-wider">{PROFILE_TYPE_LABEL[t]}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="font-display text-2xl font-bold">{n}</div>
                <div className="text-xs text-slate-500">/ {MAX_PROFILES_PER_TYPE}</div>
              </div>
              {existingProfile ? (
                <Button size="sm" variant="outline"
                  onClick={() => setToDelete(existingProfile)}
                  data-testid={`delete-type-${t}`}
                  className="mt-2 w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete profile
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled={limitReached}
                  onClick={() => nav(`/profiles/add?type=${t}`)}
                  data-testid={`add-${t}`}
                  className="mt-2 w-full text-xs">
                  {limitReached ? "Limit reached" : `Add ${PROFILE_TYPE_LABEL[t]}`}
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {profiles.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="No profiles yet"
            description="Create a Musician, Band, DJ or Venue profile to start using the marketplace."
            action={<Button onClick={() => nav("/profiles/add")} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">Create your first profile</Button>}
          />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles.map((p) => {
          const isActive = p.profile_id === account?.active_profile_id;
          return (
            <Card key={p.profile_id} className="p-5 border-slate-200" data-testid={`myprof-${p.profile_type}`}>
              <div className="flex gap-3 items-start">
                <img src={p.profile_picture_url} className="w-16 h-16 rounded-xl object-cover bg-slate-100 flex-none" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {p.display_name}
                    {isActive && <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Active</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {PROFILE_TYPE_LABEL[p.profile_type]} · {locationLine(p) || "—"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                {!isActive && (
                  <Button size="sm" variant="outline" onClick={() => switchProfile(p.profile_id)} data-testid={`activate-${p.profile_type}`}>
                    Switch to this
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => nav(`/profiles/${p.profile_id}/edit`)} data-testid={`edit-${p.profile_type}`}>
                  Edit
                </Button>
                <Link to={`/profiles/view/${p.profile_id}`}>
                  <Button size="sm" variant="ghost">View public</Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setToDelete(p)}
                  data-testid={`delete-${p.profile_type}`}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <DeleteProfileDialog
        open={!!toDelete}
        onOpenChange={(o) => { if (!o) setToDelete(null); }}
        profile={toDelete}
        onDeleted={async () => { setToDelete(null); await refresh(); await loadCounts(); }}
      />
    </AppLayout>
  );
}
